import React, { useEffect, useMemo } from 'react';
import { Box, Collapse, Stack, TextField, Typography } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NumericFormat } from 'react-number-format';
import { currencyUtils } from 'skipify-types';

import { CallbackResponse, ConfigResponse } from '../../../store/modules/merchant/types';
import { ConfigSection, ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import { InheritedConfigSettingCallouts } from '../../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../../hooks/useChildInheritedConfigEdit';
import {
  isCallbackInheritedForMerchant,
  isConfigInheritedForMerchant,
} from '../../../util/configInheritance';
import { getChildCustomizeIdleSaveBarDetailText } from '../../../util/parentSettingSave';
import { getAgenticCheckoutInheritableSaveBarLabels } from '../agenticCheckoutSaveBar';
import { useMerchant } from '../../../contexts/MerchantContext';
import { merchantDomainErrorMessage, sanitizeNumericString } from '../../../util/helpers';

const validateInputNumber = (value?: string | null) => {
  if (!value) return true;
  const number = Number(value.replaceAll(/[^\d-]/g, ''));
  return !Number.isNaN(number) && number > 0;
};

type AgenticCheckoutValue = {
  maxTransactionAmount?: number;
  maxLookupShopperCalls?: number;
  maxGeneratePaylinkCalls?: number;
};

type Props = {
  agenticCheckoutConfig?: ConfigResponse<AgenticCheckoutValue>;
  callbacks?: CallbackResponse[];
  onSave: (payload: {
    config: AgenticCheckoutValue;
    callback: { isEnabled: boolean; validateTotalCallbackUrl?: string };
    shouldSaveConfig: boolean;
    shouldSaveCallback: boolean;
  }) => Promise<boolean>;
  isSaving?: boolean;
  readOnly?: boolean;
};

const validationSchema = z
  .object({
    maxTransactionAmount: z
      .string()
      .optional()
      .refine((value) => validateInputNumber(value), { message: 'Invalid amount' }),
    maxLookupShopperCalls: z
      .string()
      .optional()
      .refine((value) => validateInputNumber(value), { message: 'Invalid number of calls' }),
    maxGeneratePaylinkCalls: z
      .string()
      .optional()
      .refine((value) => validateInputNumber(value), { message: 'Invalid number of calls' }),
    validateTotalCallbackUrl: z.string().optional(),
    isValidateTotalCallbackEnabled: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.isValidateTotalCallbackEnabled) {
      return;
    }
    const url = data.validateTotalCallbackUrl?.trim() ?? '';
    if (!url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Callback URL is required when the validate total callback is enabled',
        path: ['validateTotalCallbackUrl'],
      });
      return;
    }
    const urlError = merchantDomainErrorMessage(url);
    if (urlError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: urlError,
        path: ['validateTotalCallbackUrl'],
      });
    }
  });

type FormValues = z.infer<typeof validationSchema>;

export const AgenticCheckoutForm = ({
  agenticCheckoutConfig,
  callbacks,
  onSave,
  readOnly = false,
  isSaving = false,
}: Props) => {
  const existingValue = useMemo(
    () => agenticCheckoutConfig?.value || {},
    [agenticCheckoutConfig?.value],
  );

  const { merchantId, hasDescendants } = useMerchant();
  const validateTotalCallback = callbacks?.find(
    (cb) => cb.callbackType === 'AgenticCheckoutValidateTotal',
  );
  const validateTotalCallbackUrl = validateTotalCallback?.url;

  const configInherited = isConfigInheritedForMerchant(merchantId, agenticCheckoutConfig);
  const callbackInherited = isCallbackInheritedForMerchant(merchantId, validateTotalCallback, {
    missingIsInherited: false,
  });
  const settingInherited = configInherited || callbackInherited;
  const {
    isChildMerchant,
    showInheritedCallouts,
    isCustomizingInherited,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);
  const configFieldsDisabled =
    readOnly || (isChildMerchant && configInherited && !isCustomizingInherited);
  const callbackFieldsDisabled = readOnly || (isChildMerchant && callbackInherited);

  const formDefaultValues: FormValues = useMemo(
    () => ({
      maxTransactionAmount: existingValue.maxTransactionAmount
        ? currencyUtils.minorToMajor(existingValue.maxTransactionAmount).toFixed(2)
        : '',
      maxLookupShopperCalls: existingValue.maxLookupShopperCalls?.toString() ?? '',
      maxGeneratePaylinkCalls: existingValue.maxGeneratePaylinkCalls?.toString() ?? '',
      validateTotalCallbackUrl: validateTotalCallbackUrl ?? '',
      isValidateTotalCallbackEnabled: Boolean(validateTotalCallbackUrl),
    }),
    [
      existingValue.maxGeneratePaylinkCalls,
      existingValue.maxLookupShopperCalls,
      existingValue.maxTransactionAmount,
      validateTotalCallbackUrl,
    ],
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isDirty, isValid, errors, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: formDefaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!isDirty) {
      reset(formDefaultValues);
    }
  }, [formDefaultValues, isDirty, reset]);

  const onCancel = () => {
    reset(formDefaultValues);
    resetCustomizing();
  };

  const inheritableSaveBarLabels = useMemo(() => {
    if (!isDirty) {
      return [];
    }
    return getAgenticCheckoutInheritableSaveBarLabels(dirtyFields);
  }, [dirtyFields, isDirty]);

  const showSaveBar = isChildInheritedSaveBarOpen(isDirty);
  const saveBarInheritance = getSaveBarInheritanceProps(showSaveBar, inheritableSaveBarLabels);
  const saveBarDetailTextOverride =
    isCustomizingInherited && !isDirty
      ? getChildCustomizeIdleSaveBarDetailText(hasDescendants)
      : saveBarInheritance.detailTextOverride;

  const onFormSubmit = async (data: FormValues) => {
    if (!isDirty) {
      return;
    }
    const value: {
      config: AgenticCheckoutValue;
      callback: { isEnabled: boolean; validateTotalCallbackUrl?: string };
    } = { config: {}, callback: { isEnabled: false } };

    if (data.maxTransactionAmount) {
      const amountNumber = Number(sanitizeNumericString(data.maxTransactionAmount));
      if (!isNaN(amountNumber) && amountNumber > 0) {
        value.config.maxTransactionAmount = amountNumber;
      }
    }
    if (data.maxLookupShopperCalls) {
      const callsNumber = Number(sanitizeNumericString(data.maxLookupShopperCalls));
      if (!isNaN(callsNumber) && callsNumber > 0) {
        value.config.maxLookupShopperCalls = callsNumber;
      }
    }
    if (data.maxGeneratePaylinkCalls) {
      const callsNumber = Number(sanitizeNumericString(data.maxGeneratePaylinkCalls));
      if (!isNaN(callsNumber) && callsNumber > 0) {
        value.config.maxGeneratePaylinkCalls = callsNumber;
      }
    }

    value.callback.isEnabled = data.isValidateTotalCallbackEnabled;
    if (data.isValidateTotalCallbackEnabled) {
      value.callback.validateTotalCallbackUrl = data.validateTotalCallbackUrl?.trim() ?? '';
    } else if (data.validateTotalCallbackUrl !== undefined) {
      value.callback.validateTotalCallbackUrl = data.validateTotalCallbackUrl.trim();
    }

    const shouldSaveConfig = Boolean(
      dirtyFields.maxTransactionAmount ||
      dirtyFields.maxLookupShopperCalls ||
      dirtyFields.maxGeneratePaylinkCalls,
    );
    const shouldSaveCallback =
      !callbackInherited &&
      Boolean(dirtyFields.isValidateTotalCallbackEnabled || dirtyFields.validateTotalCallbackUrl);

    const saved = await onSave({
      config: value.config,
      callback: value.callback,
      shouldSaveConfig,
      shouldSaveCallback,
    });
    if (!saved) {
      return;
    }
    reset({
      maxTransactionAmount: data.maxTransactionAmount,
      maxLookupShopperCalls: data.maxLookupShopperCalls,
      maxGeneratePaylinkCalls: data.maxGeneratePaylinkCalls,
      isValidateTotalCallbackEnabled: data.isValidateTotalCallbackEnabled,
      validateTotalCallbackUrl: data.isValidateTotalCallbackEnabled
        ? (data.validateTotalCallbackUrl?.trim() ?? '')
        : '',
    });
    resetCustomizing();
  };

  return (
    <>
      {showInheritedCallouts ? (
        <InheritedConfigSettingCallouts
          isCustomizingInherited={isCustomizingInherited}
          readOnly={readOnly}
          onCustomize={onCustomize}
        />
      ) : null}
      <Box component="form" onSubmit={handleSubmit(onFormSubmit)} id="agentic-checkout-form">
        <ConfigSection
          title="Transaction Limits"
          description="Set the maximum transaction amount for checkouts and paylinks created via MCP."
          sx={{ mb: 4 }}
        >
          <Box sx={{ mt: 2, maxWidth: '400px' }}>
            <Controller
              name="maxTransactionAmount"
              control={control}
              render={({ field }) => (
                <NumericFormat
                  customInput={TextField}
                  variant="outlined"
                  label="Max Transaction Amount"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                  allowNegative={false}
                  disabled={configFieldsDisabled}
                  onChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  id={field.name}
                  error={!!errors.maxTransactionAmount}
                  helperText={
                    errors.maxTransactionAmount?.message ||
                    'Maximum transaction amount for a checkout/paylink via MCP'
                  }
                />
              )}
            />
          </Box>
        </ConfigSection>

        <ConfigSection
          title="Tool Call Limits"
          description="Set the maximum number of tool calls allowed per session for different MCP tools."
          sx={{ mb: 4 }}
        >
          <Stack spacing={3} sx={{ mt: 2, maxWidth: '400px' }}>
            <Controller
              name="maxLookupShopperCalls"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Max Shopper Lookups"
                  id="maxLookupShopperCalls"
                  type="number"
                  disabled={configFieldsDisabled}
                  helperText={
                    errors.maxLookupShopperCalls?.message ||
                    'Maximum allowed number of shopper lookups per session'
                  }
                  {...field}
                  error={!!errors.maxLookupShopperCalls}
                />
              )}
            />
            <Controller
              name="maxGeneratePaylinkCalls"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Max Generated Paylinks"
                  id="maxGeneratePaylinkCalls"
                  type="number"
                  disabled={configFieldsDisabled}
                  helperText={
                    errors.maxGeneratePaylinkCalls?.message ||
                    'Maximum allowed number of created paylinks per session'
                  }
                  {...field}
                  error={!!errors.maxGeneratePaylinkCalls}
                />
              )}
            />
          </Stack>
        </ConfigSection>

        <Stack sx={{ maxWidth: '448px' }} spacing={2}>
          <ConfigSwitchSection
            title="Validate Total Callback"
            description={
              <>
                You can designate a URL below where we can perform a callback to validate the total
                of the order on the pay tool call.
                <b> Please make sure to use a valid protocol (http:// or https://)</b>
                {callbackInherited ? (
                  <Typography component="p" variant="body2" sx={{ mt: 1 }}>
                    This callback is inherited from your parent merchant. You cannot change it here
                    yet; turning it off for your location will be supported in a future update.
                  </Typography>
                ) : null}
              </>
            }
            isChecked={watch('isValidateTotalCallbackEnabled')}
            onToggle={(newIsValidateTotalCallbackEnabled) => {
              setValue('isValidateTotalCallbackEnabled', newIsValidateTotalCallbackEnabled, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (!newIsValidateTotalCallbackEnabled) {
                setValue('validateTotalCallbackUrl', '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
              void trigger(['isValidateTotalCallbackEnabled', 'validateTotalCallbackUrl']);
            }}
            renderSwitchLabel={() => (
              <Typography component="span">
                {`Callback URL is currently `}
                <Typography sx={{ fontWeight: 'bold' }} component="span">
                  {`${watch('isValidateTotalCallbackEnabled') ? 'enabled ' : 'disabled '}`}
                </Typography>
              </Typography>
            )}
            disabled={callbackFieldsDisabled}
          />
          <Collapse in={watch('isValidateTotalCallbackEnabled')}>
            <Box sx={{ mt: 2, maxWidth: '448px' }}>
              <Controller
                name="validateTotalCallbackUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Validate Total Callback URL"
                    id="validateTotalCallbackUrl"
                    type="text"
                    disabled={callbackFieldsDisabled}
                    error={!!errors.validateTotalCallbackUrl}
                    helperText={
                      errors.validateTotalCallbackUrl?.message ||
                      'Callback URL to validate the total of the checkout/paylink.'
                    }
                    fullWidth
                    {...field}
                  />
                )}
              />
            </Box>
          </Collapse>
        </Stack>
      </Box>

      <ParentAwareSaveBar
        formId="agentic-checkout-form"
        show={showSaveBar && !readOnly}
        saveLoading={isSaving}
        onSave={handleSubmit(onFormSubmit)}
        onCancel={onCancel}
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarDetailTextOverride}
        disableSave={isSaving || !isDirty || (isDirty && !isValid)}
      />
    </>
  );
};
